import React from 'react';
import {Link} from 'react-router-dom';
import {DateTime} from 'luxon';

import {Pagination} from 'Components/Partials';

/*
Small building blocks shared by the staff folder tabs, so every tab looks the same.
*/

/**
 * "2024-03-04", an ISO string or {iso_string} as "4 Mar 2024".
 *
 * @param {string|object|null} value
 * @return {string}
 */
const formatDate = (value) => {
    const iso = typeof value === 'string' ? value : value?.iso_string;

    if (!iso) {
        return '';
    }

    const date = DateTime.fromISO(iso);

    return date.isValid ? date.toFormat('d LLL yyyy') : '';
};

/**
 * 1 -> "1 day", 2.5 -> "2.5 days".
 *
 * @param {number} count
 * @param {string} one
 * @param {string|null} many
 * @return {string}
 */
const plural = (count, one, many = null) => {
    const value = Number(count ?? 0);

    return `${Number.isInteger(value) ? value : value.toFixed(1).replace(/\.0$/, '')} ${value === 1 ? one : (many ?? one + 's')}`;
};

/**
 * A list of names as "A, B and C".
 *
 * @param {string[]} items
 * @return {string}
 */
const joinNames = (items = []) => {
    if (items.length <= 1) {
        return items[0] ?? '';
    }

    return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
};

const TONES = {
    neutral: 'bg-gray-100 text-gray-800',
    good: 'bg-green-100 text-green-800',
    warn: 'bg-yellow-100 text-yellow-800',
    bad: 'bg-red-100 text-red-800',
    info: 'bg-blue-100 text-blue-800',
};

/**
 * @function Pill
 * @param {string} tone
 * @param {*} children
 * @return {JSX.Element}
 * @constructor
 */
const Pill = ({tone = 'neutral', children}) => (
    <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap ${TONES[tone]}`}>
        {children}
    </span>
);

/**
 * A white card with a heading and an optional link or button on the right.
 *
 * @function Card
 * @return {JSX.Element}
 * @constructor
 */
const Card = ({title, id, action = null, subtitle = null, className = '', children}) => (
    <section aria-labelledby={id} className={`bg-white border border-gray-200 rounded-2xl p-5 ${className}`}>
        {(title || action) && (
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                    {title && <h2 id={id} className="font-league-spartan font-bold text-xl">{title}</h2>}
                    {subtitle && <div className="text-gray-600 text-sm">{subtitle}</div>}
                </div>
                {action}
            </div>
        )}
        {children}
    </section>
);

/**
 * A link styled as a small button, e.g. "Open" or "Edit".
 *
 * @function SmallLink
 * @return {JSX.Element}
 * @constructor
 */
const SmallLink = ({to, children}) => (
    <Link to={to} className="font-bold text-app-leading hover:underline whitespace-nowrap">
        {children}
    </Link>
);

/**
 * @function SmallButton
 * @return {JSX.Element}
 * @constructor
 */
const SmallButton = ({onClick, primary = false, disabled = false, children}) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`rounded-full px-4 py-2 font-bold text-sm whitespace-nowrap disabled:opacity-50
            ${primary ? 'bg-app-supporting-yellow text-white' : 'bg-white border-2 border-app-leading text-app-leading'}`}
    >
        {children}
    </button>
);

/**
 * Label / value pairs.
 *
 * @function Details
 * @param {array} items [[label, value], ...]
 * @return {JSX.Element}
 * @constructor
 */
const Details = ({items}) => (
    <dl className="divide-y divide-gray-100">
        {items.map(([label, value], i) => (
            <div key={i} className="flex flex-wrap justify-between gap-x-6 gap-y-1 py-2">
                <dt className="text-gray-600">{label}</dt>
                <dd className="font-bold text-right">
                    {value === null || value === undefined || value === '' ?
                        <span className="font-normal text-gray-500">Not given</span> :
                        value}
                </dd>
            </div>
        ))}
    </dl>
);

/**
 * A big figure with a label above and a note below.
 *
 * @function Stat
 * @return {JSX.Element}
 * @constructor
 */
const Stat = ({label, value, note = null, tone = null}) => (
    <div className={`rounded-2xl p-4 border ${tone === 'warn' ? 'bg-yellow-50 border-yellow-200' : 'bg-white border-gray-200'}`}>
        <div className="text-sm text-gray-600">{label}</div>
        <div className="font-league-spartan font-bold text-3xl leading-tight">{value}</div>
        {note && <div className="text-sm text-gray-600">{note}</div>}
    </div>
);

/**
 * A thin bar showing how much of something is used.
 *
 * @function Meter
 * @return {JSX.Element}
 * @constructor
 */
const Meter = ({value, max, label}) => {
    const percent = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;

    return (
        <div role="img" aria-label={label} className="mt-2 h-2.5 rounded-full bg-gray-200 overflow-hidden">
            <div className="h-2.5 bg-app-leading" style={{width: percent + '%'}}/>
        </div>
    );
};

/**
 * "Showing 1 to 20 of 45" and page buttons, for lists paged on the server or in the browser.
 *
 * @function PageBar
 * @param {number} page
 * @param {number} perPage
 * @param {number} total
 * @param {function} goToPage
 * @return {JSX.Element|null}
 * @constructor
 */
const PageBar = ({page, perPage, total, goToPage}) => {
    if (!total) {
        return null;
    }

    const lastPage = Math.max(1, Math.ceil(total / perPage));
    const from = (page - 1) * perPage + 1;
    const to = Math.min(total, page * perPage);

    return (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
            <span className="text-sm text-gray-600">Showing {from} to {to} of {total}</span>
            {lastPage > 1 && <Pagination page={page} pageCount={lastPage} goToPage={goToPage} />}
        </div>
    );
};

/**
 * Pages a list held in the browser. Children is a function given the items for the current page.
 * Goes back to page 1 when the list changes length (e.g. a filter is applied).
 */
class Paged extends React.Component {
    state = {page: 1};

    componentDidUpdate(prevProps) {
        if (prevProps.items.length !== this.props.items.length && this.state.page !== 1) {
            this.setState({page: 1});
        }
    }

    render() {
        const {items, perPage = 20, children} = this.props;
        const lastPage = Math.max(1, Math.ceil(items.length / perPage));
        const page = Math.min(this.state.page, lastPage);

        return (
            <>
                {children(items.slice((page - 1) * perPage, page * perPage))}
                <PageBar page={page} perPage={perPage} total={items.length} goToPage={(next) => this.setState({page: next})} />
            </>
        );
    }
}

/**
 * A simple table with headings, for the folder's lists. Pages 20 rows at a time;
 * pass perPage={0} when the list is already paged on the server.
 *
 * @function SimpleTable
 * @param {string[]} headings
 * @param {array} rows Arrays of cells.
 * @param {string} empty
 * @param {number} perPage
 * @return {JSX.Element}
 * @constructor
 */
const SimpleTable = ({headings, rows, empty = 'Nothing to show.', footer = null, perPage = 20}) => {
    const table = (pageRows) => (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-left border-b-2 border-gray-200">
                        {headings.map((heading, i) => (
                            <th key={i} scope="col" className="py-2 pr-4 font-bold text-gray-600 whitespace-nowrap">{heading}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {pageRows.length === 0 && (
                        <tr><td colSpan={headings.length} className="py-4 text-gray-600">{empty}</td></tr>
                    )}
                    {pageRows.map((cells, i) => (
                        <tr key={i} className="border-b border-gray-100 hover:bg-blue-50">
                            {cells.map((cell, j) => (
                                <td key={j} className="py-3 pr-4 align-middle">{cell}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
                {footer && <tfoot>{footer}</tfoot>}
            </table>
        </div>
    );

    return perPage ? <Paged items={rows} perPage={perPage}>{table}</Paged> : table(rows);
};

export {formatDate, plural, joinNames, Pill, Card, SmallLink, SmallButton, Details, Stat, Meter, SimpleTable, Paged, PageBar};
